import React from 'react';
import {
  UnitRateType,
  workforceGroupUnitRatesLogType,
} from '@scheduling/graphql';
import {
  Collapse,
  Text,
  Title,
  Tooltip,
  Col,
  Row,
  Divider,
} from '@common/components';
import { ColorsType } from '@common/tokens';
import { IncentiveRatesViewDrawerUnitRatesRow } from './_incentive-rates-view-drawer-unit-rates-row';

export function IncentiveRatesViewDrawerRatesLog({
  data: {
    workforceGroup,
    organizationUnit,
    effectiveRate,
    noLongerInEffectRates,
    upcomingRates,
  },
}: {
  data: workforceGroupUnitRatesLogType;
}) {
  const noLongerInEffectRatesWithoutEmpty = noLongerInEffectRates.filter(
    ({ type }) => type !== 'empty'
  );

  function getActiveKeysAsList(activeKeysAsObject: Record<string, boolean>) {
    return Object.entries(activeKeysAsObject)
      .filter(([, value]) => value)
      .map(([key]) => key);
  }

  function isLastIndex(array: UnitRateType[], index: number) {
    return array.length - 1 === index;
  }

  function isLastUpcomingRate(index: number) {
    return isLastIndex(upcomingRates, index);
  }

  function isLastNoLongerInEffectRate(index: number) {
    return isLastIndex(noLongerInEffectRatesWithoutEmpty, index);
  }

  const effectiveRatesDisabled = !effectiveRate;

  const defaultActiveKey = getActiveKeysAsList({
    effective: !effectiveRatesDisabled,
    upcoming: upcomingRates.length > 0,
  });

  return (
    <Row gutter={[8, 16]}>
      <Col span={24}>
        <Title>{workforceGroup.name}</Title>
      </Col>
      <Col span={24}>
        <Row>
          <Col span={3}>
            <Label label="Unit" color="midnightLighter" />
          </Col>
          <Col span={21}>{organizationUnit.name}</Col>
        </Row>
      </Col>
      <Col span={24}>
        <Divider gutter={16} />
        <EffectiveRatesCollapse
          effectiveRate={effectiveRate}
          disabled={effectiveRatesDisabled}
          defaultActiveKey={defaultActiveKey}
        />
        <Collapse defaultActiveKey={defaultActiveKey}>
          {!!upcomingRates.length && (
            <Collapse.Panel
              key="upcoming"
              title={<Label label="Upcoming Rates" color="elm" />}
            >
              {upcomingRates.map((unitRates, index) => (
                <React.Fragment key={unitRates.id}>
                  <IncentiveRatesViewDrawerUnitRatesRow unitRates={unitRates} />
                  {!isLastUpcomingRate(index) && <Divider gutter={16} />}
                </React.Fragment>
              ))}
            </Collapse.Panel>
          )}
          {!!noLongerInEffectRatesWithoutEmpty.length && (
            <Collapse.Panel
              key="no-longer-in-effect"
              title={<Label label="Inactive Rates" color="elm" />}
            >
              {noLongerInEffectRatesWithoutEmpty.map((unitRates, index) => (
                <React.Fragment key={unitRates.id}>
                  <IncentiveRatesViewDrawerUnitRatesRow unitRates={unitRates} />
                  {!isLastNoLongerInEffectRate(index) && (
                    <Divider gutter={16} />
                  )}
                </React.Fragment>
              ))}
            </Collapse.Panel>
          )}
        </Collapse>
      </Col>
    </Row>
  );
}

function EffectiveRatesCollapse({
  effectiveRate,
  disabled,
  defaultActiveKey,
}: {
  effectiveRate: UnitRateType;
  disabled: boolean;
  defaultActiveKey: string[];
}) {
  const collapse = (
    <Collapse defaultActiveKey={defaultActiveKey}>
      <Collapse.Panel
        key="effective"
        title={
          <Label
            label="Active Rates"
            color={disabled ? 'steelDarker' : 'elm'}
          />
        }
        collapsible={disabled ? 'disabled' : undefined}
      >
        <React.Fragment key={effectiveRate.id}>
          <IncentiveRatesViewDrawerUnitRatesRow unitRates={effectiveRate} />
        </React.Fragment>
      </Collapse.Panel>
    </Collapse>
  );

  if (!disabled) {
    return collapse;
  }

  return (
    <Tooltip placement="topLeft" title="No active rates available">
      {collapse}
    </Tooltip>
  );
}

function Label({ label, color }: { label: string; color: ColorsType }) {
  return (
    <Text size="lg" color={color}>
      {label}
    </Text>
  );
}
